<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { NSpin, NAlert } from 'naive-ui'
import PageHeader from '@/components/commons/PageHeader.vue'
import PursuitFilters from '@/components/pursuit/PursuitFilters.vue'
import PursuitTable from '@/components/pursuit/PursuitTable.vue'
import PursuitPagination from '@/components/pursuit/PursuitPagination.vue'
import TopPursuersCards from '@/components/pursuit/TopPursuersCards.vue'
import EmptyStateIllustration from '@/components/commons/EmptyStateIllustration.vue'
import ExportButton from '@/components/commons/ExportButton.vue'
import { usePursuitFiltersUrl } from '@/composables/usePursuitFiltersUrl'
import { usePursuitRanker } from '@/api/pursuit'
import { formatDateTime } from '@/utils/formatDate'

const { filters } = usePursuitFiltersUrl()
const queryClient = useQueryClient()
const router = useRouter()
const route = useRoute()

const { data, isLoading, isError, error, isFetching } = usePursuitRanker(filters)

function onRefresh() {
  queryClient.invalidateQueries({ queryKey: ['pursuit', 'ranker'] })
}

function onSelect(plate: string) {
  // Сохраняем текущее окно рейтинга (since/until в URL), чтобы карточка номера
  // открылась в том же периоде, что и таблица (PursuitTable делает так же).
  router.push({ name: 'pursuit-plate', params: { plate }, query: route.query })
}

const summaryText = computed(() => {
  if (!data.value) return ''
  return `Найдено ${data.value.total_plates_in_range} номеров за период ${formatDateTime(data.value.since)} — ${formatDateTime(data.value.until)}`
})
</script>

<template>
  <div class="pursuit-page">
    <PageHeader title="Преследователи" :sub="summaryText" />

    <div class="filters-bar">
      <PursuitFilters @refresh="onRefresh" />
      <span v-if="isFetching && !isLoading" class="updating">⟳ обновляется…</span>
      <ExportButton
        path="/export/pursuit.xlsx"
        :params="{
          since: filters.since?.toISOString?.() ?? undefined,
          until: filters.until?.toISOString?.() ?? undefined,
          min_appearances: filters.min_appearances ?? 0,
        }"
        :filename="`dozor-pursuit-${new Date().toISOString().slice(0,10)}.xlsx`"
      />
    </div>

    <n-spin :show="isLoading">
      <n-alert v-if="isError" type="error" style="margin-bottom: 12px">
        Ошибка загрузки: {{ error?.message ?? 'неизвестная' }}
        <button class="retry" @click="onRefresh">Повторить</button>
      </n-alert>

      <template v-else-if="data">
        <div v-if="data.total_plates_in_range === 0" class="empty-fallback">
          <EmptyStateIllustration
            type="no-pursuits"
            title="Преследователей не найдено"
            description="Попробуйте расширить период или снизить минимум эпизодов."
          />
        </div>
        <template v-else>
          <!-- Топ-3 карточки — только на первой странице (глобальный топ по баллу). -->
          <TopPursuersCards v-if="filters.page === 1" :items="data.items" @select="onSelect" />
          <PursuitTable :items="data.items" :loading="isLoading" />
          <PursuitPagination :total="data.total_plates_in_range" />
        </template>
      </template>
    </n-spin>

  </div>
</template>

<style scoped>
.pursuit-page { padding: var(--sp-5); }
.filters-bar {
  /* По правке заказчика 2026-06-11 (как на «Истории»): обычный блок страницы,
     уезжает вместе со скроллом — sticky-поведение убрано. */
  margin-bottom: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-1);
}
.updating { font-size: var(--fs-sm); color: var(--muted); }
.retry { margin-left: 12px; padding: 2px 10px; border: 1px solid currentColor; background: transparent; border-radius: 3px; cursor: pointer; }
.empty-fallback { margin-top: var(--sp-4); }
@media (max-width: 767px) {
  .pursuit-page { padding: var(--sp-4); }
  .filters-bar { flex-direction: column; align-items: stretch; }
}
</style>
